import { useGetProjectPermissionsQuery } from '@/model/workspace/api';
import { useGetApiWorkspacesByIdPermissionsQuery } from '@/model/workspace/api.generated';
import type { Permission } from '@/model/workspace/permission.generated';
import { type WorkspaceRole, toWorkspaceRole } from '@/model/workspace/workspace.model';
import { useAppSelector } from '@/shared/hooks/useStore';
import { selectCurrentProject } from '@/viewmodel/features/project/state/projectSlice';
import { selectCurrentWorkspace } from '@/viewmodel/features/workspace';
import { useCallback, useMemo } from 'react';

interface UsePermissionsResult {
  can: (permission: Permission) => boolean;
  role: WorkspaceRole | null;
  permissions: Permission[];
  isLoading: boolean;
}

export function usePermissions(): UsePermissionsResult {
  const workspace = useAppSelector(selectCurrentWorkspace);
  const project = useAppSelector(selectCurrentProject);

  const workspaceId = workspace?.id ?? '';
  const projectId = project?.id ?? '';

  const { data: workspacePerms, isLoading: wsLoading } = useGetApiWorkspacesByIdPermissionsQuery(
    { id: workspaceId },
    { skip: !workspaceId },
  );

  const { data: projectPerms, isLoading: projLoading } = useGetProjectPermissionsQuery(
    { projectId },
    { skip: !projectId },
  );

  const effectiveData = projectId ? (projectPerms ?? workspacePerms) : workspacePerms;

  const permissions = useMemo(() => effectiveData?.permissions ?? [], [effectiveData]);
  const role = toWorkspaceRole(effectiveData?.role);
  const isLoading = wsLoading || (!!projectId && projLoading);

  const can = useCallback(
    (permission: Permission): boolean => permissions.includes(permission),
    [permissions],
  );

  return { can, role, permissions, isLoading };
}
